/* ─────────────────────────────────────────────────────────────
   hb-custom.js
   JS partagé par toutes les sections custom HBIOMED.

   Fournit :
     - HB.revealOnScroll() : IntersectionObserver pour .hb-reveal
     - HB.Slideshow : carousel auto/manual + dots (pour hero-slideshow)
     - HB.Carousel  : scroll-snap horizontal + dots (pour temoignages, etc.)
     - HB.initAll() : init tout au DOMContentLoaded

   Compatibilité : tous browsers modernes (ES2017+, IntersectionObserver
   natif). Pas de dépendance externe.
   ───────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  const HB = (window.HB = window.HB || {});

  /* ─── Reveal au scroll ───────────────────────────────────── */
  HB.revealOnScroll = function () {
    const els = document.querySelectorAll('.hb-reveal');
    if (!els.length || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    els.forEach((el) => io.observe(el));
  };

  /* ─── Slideshow plein écran (hero) ───────────────────────── */
  HB.Slideshow = class {
    constructor(root) {
      this.root = root;
      this.slides = Array.from(root.querySelectorAll('[data-hb-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-hb-dot]'));
      this.autoplay = parseInt(root.dataset.hbAutoplay || '5000', 10);
      this.current = 0;
      this.timer = null;
      this.paused = false;
      if (this.slides.length <= 1) return;
      this.bind();
      this.go(0);
      this.play();
    }
    bind() {
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          this.go(i);
          this.resetAutoplay();
          dot.focus({ preventScroll: true });
        });
        // ARIA tab pattern : flèches gauche/droite naviguent entre tabs
        dot.addEventListener('keydown', (e) => {
          let next = i;
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = i - 1;
          else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = i + 1;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = this.dots.length - 1;
          else return;
          e.preventDefault();
          next = (next + this.dots.length) % this.dots.length;
          this.go(next);
          this.dots[next].focus({ preventScroll: true });
          this.resetAutoplay();
        });
      });
      this.root.addEventListener('mouseenter', () => (this.paused = true));
      this.root.addEventListener('mouseleave', () => (this.paused = false));
      // Pause si focus dans le slideshow (utilisateur clavier)
      this.root.addEventListener('focusin', () => (this.paused = true));
      this.root.addEventListener('focusout', () => (this.paused = false));
      // Pause si l'onglet n'est pas visible (économie batterie mobile)
      document.addEventListener('visibilitychange', () => {
        this.paused = document.hidden;
      });
    }
    go(i) {
      this.current = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((s, idx) => {
        const active = idx === this.current;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      this.dots.forEach((d, idx) => {
        const active = idx === this.current;
        d.classList.toggle('is-active', active);
        d.setAttribute('aria-selected', active ? 'true' : 'false');
        // Roving tabindex (ARIA tab pattern) : seul l'actif est tabbable
        d.setAttribute('tabindex', active ? '0' : '-1');
      });
    }
    next() { if (!this.paused) this.go(this.current + 1); }
    play() {
      if (!this.autoplay) return;
      this.timer = setInterval(() => this.next(), this.autoplay);
    }
    resetAutoplay() {
      clearInterval(this.timer);
      this.play();
    }
  };

  /* ─── Carrousel scroll-snap (témoignages, presse) ────────── */
  HB.Carousel = class {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-hb-carousel-track]');
      this.prev = root.querySelector('[data-hb-carousel-prev]');
      this.next = root.querySelector('[data-hb-carousel-next]');
      if (!this.track) return;
      this.bind();
    }
    bind() {
      const step = () => {
        const first = this.track.children[0];
        if (!first) return 320;
        const style = window.getComputedStyle(this.track);
        const gap = parseInt(style.gap || style.columnGap || 0, 10);
        return first.getBoundingClientRect().width + gap;
      };
      if (this.prev) {
        this.prev.addEventListener('click', () => {
          this.track.scrollBy({ left: -step(), behavior: 'smooth' });
        });
      }
      if (this.next) {
        this.next.addEventListener('click', () => {
          this.track.scrollBy({ left: step(), behavior: 'smooth' });
        });
      }
    }
  };

  /* ─── Initialisation globale ─────────────────────────────── */
  HB.initAll = function () {
    HB.revealOnScroll();
    document.querySelectorAll('[data-hb-slideshow]').forEach((el) => new HB.Slideshow(el));
    document.querySelectorAll('[data-hb-carousel]').forEach((el) => new HB.Carousel(el));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', HB.initAll);
  } else {
    HB.initAll();
  }

  // Réinit après chaque section_load (theme editor preview)
  document.addEventListener('shopify:section:load', HB.initAll);
})();
